import React, { KeyboardEvent, useRef, useState, useEffect, useCallback } from 'react';
import { IconButton, Input, useStyles2 } from '@grafana/ui';
import { css, cx, keyframes } from '@emotion/css';
import { GrafanaTheme2 } from '@grafana/data';
import { reportInteraction } from '@grafana/runtime';
import { ChatContextItem } from '../../context/types';
import { useAssistant } from '../../hook';
import { OpenAssistantProps } from '../../sidebar';
import { createBorderAnimation, getBackgroundGradientColors } from '../../utils/theme';
import { useAnimatedPlaceholder, DEFAULT_EXAMPLE_PROMPTS } from './useAnimatedPlaceholder';

export interface AssistantPromptCardProps {
  /** Origin of the request for analytics tracking. Should be a structured string using forward slashes. */
  origin: string;
  /** Context items passed to the assistant on submit. Created with `createAssistantContextItem`. */
  context?: ChatContextItem[];
  /** Assistant mode (e.g. 'dashboarding'). Defaults to 'assistant'. */
  mode?: string;
  /** Placeholder text for the input. */
  placeholder?: string;
  /** Example prompts to cycle through as animated placeholder text. Pass an empty array to disable. */
  examplePrompts?: string[];
  /** Called when the card should close (Escape key, close button). */
  onClose?: () => void;
  /** Called after a prompt is submitted, receives the prompt string. */
  onSubmit?: (prompt: string) => void;
  /** Called when the user clicks the card body. */
  onClick?: () => void;
  /** Whether the card border is animated. Defaults to true. */
  animated?: boolean;
  /** Extra CSS class for the root element. */
  className?: string;
}

/**
 * A prompt card component that opens the Grafana Assistant with a user-typed prompt and context.
 * Renders nothing if the assistant is not available. Positioning is the consumer's responsibility.
 */
export function AssistantPromptCard(props: AssistantPromptCardProps) {
  const { isAvailable, openAssistant } = useAssistant();
  if (!isAvailable || !openAssistant) {
    return null;
  }
  return <AssistantPromptCardView {...props} openAssistant={openAssistant} />;
}

export interface AssistantPromptCardViewProps extends AssistantPromptCardProps {
  openAssistant: (props: OpenAssistantProps) => void;
}

export function AssistantPromptCardView({
  origin,
  context,
  mode,
  placeholder,
  examplePrompts,
  animated = true,
  onClose,
  onSubmit,
  onClick,
  className,
  openAssistant,
}: AssistantPromptCardViewProps) {
  const [prompt, setPrompt] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const styles = useStyles2(getStyles);
  const resolvedPrompts = examplePrompts ?? (placeholder !== undefined ? [] : DEFAULT_EXAMPLE_PROMPTS);
  const staticPlaceholder = placeholder ?? 'Ask Assistant...';
  const animatedPlaceholder = useAnimatedPlaceholder(staticPlaceholder, resolvedPrompts, prompt.length > 0);

  useEffect(() => {
    reportInteraction('grafana_assistant_app_prompt_card_displayed', { from: origin });
  }, [origin]);

  useEffect(() => {
    const rafId = requestAnimationFrame(() => inputRef.current?.focus());
    return () => cancelAnimationFrame(rafId);
  }, [context]);

  const submit = useCallback(
    (text: string) => {
      const trimmed = text.trim();
      if (!trimmed) {
        return;
      }

      openAssistant({ prompt: trimmed, context, autoSend: true, origin, mode });
      onSubmit?.(trimmed);
      setPrompt('');
    },
    [openAssistant, context, origin, mode, onSubmit]
  );

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit(prompt);
    }

    if (e.key === 'Escape') {
      e.preventDefault();
      onClose?.();
    }
  };

  return (
    <div
      className={cx(animated ? styles.card : styles.cardStatic, className)}
      onClick={onClick}
      role="search"
      aria-label={staticPlaceholder}
      data-testid="assistant-prompt-card"
    >
      <div className={styles.inputRow}>
        <Input
          ref={inputRef}
          value={prompt}
          onChange={(e) => setPrompt(e.currentTarget.value)}
          onKeyDown={handleKeyDown}
          placeholder={animatedPlaceholder}
          suffix={
            <IconButton
              name="ai-sparkle"
              onClick={() => submit(prompt)}
              disabled={!prompt.trim()}
              tooltip="Send to assistant"
              aria-label="Send to assistant"
              data-testid="assistant-prompt-card-send"
            />
          }
          data-testid="assistant-prompt-card-input"
        />
      </div>
    </div>
  );
}

function getGlowShadow(theme: GrafanaTheme2) {
  const baseShadow = theme.isDark ? 'rgb(17 18 23)' : 'rgb(245 245 245)';
  return [
    'rgba(168, 85, 247, 0.25) 3px -1px 20px',
    'rgba(249, 115, 22, 0.18) -5px 3px 40px',
    'rgba(168, 85, 247, 0.12) 7px 4px 60px',
    `${baseShadow} 0 6px 30px`,
  ].join(', ');
}

const getStyles = (theme: GrafanaTheme2) => {
  const backgroundColor = theme.colors.background.primary;
  const [gradientColor1, gradientColor2] = getBackgroundGradientColors(theme);

  const borderAnimation = keyframes({
    label: 'prompt-card-border',
    ...createBorderAnimation(theme, backgroundColor),
  });

  const cardBase = {
    label: 'assistant-prompt-card',
    position: 'relative' as const,
    display: 'flex',
    flexDirection: 'column' as const,
    gap: theme.spacing(1),
    padding: theme.spacing(1),
    borderRadius: theme.shape.radius.lg,
    boxShadow: getGlowShadow(theme),
  };

  return {
    card: css({
      ...cardBase,
      cornerShape: 'squircle',
      background: backgroundColor,
      border: '1px solid transparent',
      backgroundImage: `
        linear-gradient(${backgroundColor}, ${backgroundColor}),
        conic-gradient(from 0deg, transparent 60%, ${gradientColor1} 80%, ${gradientColor2} 100%, transparent 15%)
      `,
      backgroundOrigin: 'border-box',
      backgroundClip: 'padding-box, border-box',
      [theme.transitions.handleMotion('no-preference')]: {
        animation: `${borderAnimation} 2s linear infinite`,
      },
    }),
    cardStatic: css({
      ...cardBase,
      label: 'assistant-prompt-card-static',
      background: theme.colors.background.elevated,
      border: `1px solid ${theme.colors.border.weak}`,
    }),
    inputRow: css({
      label: 'prompt-card-input-row',
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(0.5),
      '& input': {
        border: 'none !important',
      },
    }),
  };
};
